import InputMask from 'comigo-tech-react-input-mask';
import _ from 'lodash';
import { useCallback, useState } from 'react';
import { useAlert } from 'react-alert';
import { SubmitHandler, useForm } from 'react-hook-form';
import { User } from '../../../API';
import { Button } from '../../../components/button/Button';
import useActiveCampaign from '../../../hooks/useActiveCampaign';
import { createAuthAccount } from '../../../services/users';
import { splitName } from '../../../utils';

export function SignupForm({ onSignupSuccess }: { onSignupSuccess: (arg: unknown) => void }) {
  type SignupFormInput = { name: string; email: string; phone: string; password: string };
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignupFormInput>();
  const activeCampaign = useActiveCampaign();

  const handleSignup: SubmitHandler<SignupFormInput> = useCallback(
    async (data) => {
      setLoading(true);
      if (data.email !== '' && data.password !== '') {
        try {
          //do something here
          const { email, password, phone, name } = data;
          const firstName = splitName(name)[0] ?? null;
          const lastName = splitName(name)[1] ?? null;
          const user = await createAuthAccount({
            email,
            password,
            phone,
            firstName,
            lastName,
          });
          if ((user as User)?.id) {
            onSignupSuccess && onSignupSuccess(user);
          }
          if (((user as any).code as string) === 'UsernameExistsException') alert.error(`An account with the email ${email} already exists`);
          setLoading(false);
        } catch (error) {
          setLoading(false);
          if (_.has(error, 'code')) {
            switch (_.get(error, 'code')) {
              case 'NotAuthorizedException':
                alert.error('Invalid email or password');
                break;

              case 'UserNotFoundException':
                alert.error('An account with this email address does not exist');
                break;
            }
          }
        }
      }
    },
    [alert, onSignupSuccess]
  );

  return (
    <form action="#" className="w-full" onSubmit={handleSubmit(handleSignup)} data-testid="signupForm">
      <div className="mt-4 w-full">
        <label htmlFor="username">
          Full name
          <input data-testid="nameInput" id="name" type="text" placeholder="e.g. Kiah Kate" {...register('name', { required: true })} />
          {errors['name'] && <span className="text-sm text-ginger font-raleway-italic">Name is required</span>}
        </label>
      </div>
      <div className="mt-4 w-full">
        <label htmlFor="username">
          Email
          <input
            data-testid="emailInput"
            id="username"
            type="text"
            placeholder="e.g. you@gmail.com"
            {...register('email', { required: true, pattern: /^([a-zA-Z0-9_\-.+]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/gim })}
          />
          {errors['email'] && <span className="text-sm text-ginger font-raleway-italic">A valid email address is required</span>}
        </label>
      </div>
      <div className="mt-4 w-full">
        <label htmlFor="username">
          Phone number
          <InputMask mask="999-999-9999" type="tel" {...register('phone', { required: true })} data-testid="phoneNumberInput" />
          {errors['phone'] && <span className="text-sm text-ginger font-raleway-italic">phone number is required</span>}
        </label>
      </div>
      <div className="mt-4 w-full">
        <label htmlFor="password">
          Password
          <input data-testid="passwordInput" id="password" type="password" placeholder="*******" {...register('password', { required: true, minLength: 8 })} />
          <span className="text-xs">Minimum of 8 characters</span>
          {errors['password'] && (
            <span className="text-sm text-ginger font-raleway-italic block">Password is required and must be a minumum of 8 characters</span>
          )}
        </label>
      </div>
      <div className="mt-4 flex items-center place-content-between w-full text-[11px]">
        <div className="flex">
          <input type="checkbox" className="mr-2" data-testid="termsCheckbox" /> I agree to Flourish's &nbsp;
          <a href="https://www.weflourish.com/terms-and-conditions" target="_blank" rel="noreferrer" className="underline">
            Terms of Service
          </a>
          &nbsp;&&nbsp;
          <a href="https://www.weflourish.com/privacy-policy" target="_blank" rel="noreferrer" className="underline" data-testid="privacyPolicyLink">
            Privacy Policy
          </a>
        </div>
      </div>
      <div className="mt-10 w-full">
        {!loading && <Button text="Sign up & Continue" primary classes="font-semibold" type="submit" testID="signupSubmitButton" />}
        {loading && <span>Loading...</span>}
      </div>
    </form>
  );
}
